// Builds the static faceplate (panels + controls) from the parameter schema.
// Controls are tagged with data-param so bindings.ts (Phase 11) can wire them to
// the engine. No audio here — controls just emit input/change events.

import './components/Panel';
import { Knob } from './components/Knob';
import { Slider } from './components/Slider';
import {
  getSpec,
  getNumericSpec,
  modulatableParamIds,
  getDefaultPatch,
  NO_DEST,
  type ParamId,
} from '../engine';

const defaults = getDefaultPatch();

function knob(id: ParamId): Knob {
  const spec = getNumericSpec(id);
  const el = document.createElement('synth-knob') as Knob;
  el.dataset.param = id;
  el.configure({
    label: spec.label,
    min: spec.min,
    max: spec.max,
    value: defaults[id] as number,
    default: spec.default,
    taper: spec.taper,
    step: spec.kind === 'stepped' ? (spec.step ?? 1) : 0,
    unit: spec.unit,
  });
  return el;
}

function slider(id: ParamId): Slider {
  const spec = getNumericSpec(id);
  const el = document.createElement('synth-slider') as Slider;
  el.dataset.param = id;
  el.configure({
    label: spec.label,
    min: spec.min,
    max: spec.max,
    value: defaults[id] as number,
    default: spec.default,
    taper: spec.taper,
    unit: spec.unit,
  });
  return el;
}

function select(id: ParamId, options: Array<[string, string]>): HTMLElement {
  const spec = getSpec(id);
  const wrap = document.createElement('div');
  wrap.className = 'ctl';
  const label = document.createElement('span');
  label.className = 'ctl-label';
  label.textContent = spec.label;
  const sel = document.createElement('select');
  sel.dataset.param = id;
  const current = String(defaults[id]);
  for (const [text, value] of options) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = text;
    if (value === current) opt.selected = true;
    sel.appendChild(opt);
  }
  wrap.append(label, sel);
  return wrap;
}

function prettyDest(id: ParamId): string {
  const spec = getSpec(id);
  return `${spec.group.toUpperCase()} ${spec.label}`;
}

function panel(title: string, accent: string, ...children: Node[]): HTMLElement {
  const p = document.createElement('synth-panel');
  p.dataset.title = title;
  p.dataset.accent = accent;
  p.append(...children);
  return p;
}

function scopeCanvas(index: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.className = 'scope';
  c.dataset.scope = String(index);
  c.width = 220;
  c.height = 64;
  return c;
}

export function buildFaceplate(root: HTMLElement): void {
  root.replaceChildren();

  const oscPanel = (n: 1 | 2 | 3, accent: string, label: string): HTMLElement => {
    const g = `osc${n}` as const;
    return panel(
      `${label}`,
      accent,
      knob(`${g}.octave`),
      knob(`${g}.shape`),
      knob(`${g}.detune`),
      knob(`${g}.level`),
      knob(`${g}.drive`),
      scopeCanvas(n - 1),
    );
  };

  root.append(
    oscPanel(1, '1', 'OSC 1 · Analog'),
    oscPanel(2, '2', 'OSC 2 · Wavetable'),
    oscPanel(3, '3', 'OSC 3 · FM'),

    panel(
      'Filter',
      '1',
      knob('filter.cutoff'),
      knob('filter.resonance'),
      knob('filter.envAmount'),
    ),

    panel(
      'Amp Env',
      '4',
      slider('ampEnv.attack'),
      slider('ampEnv.decay'),
      slider('ampEnv.sustain'),
      slider('ampEnv.release'),
    ),
    panel(
      'Filter Env',
      '4',
      slider('filterEnv.attack'),
      slider('filterEnv.decay'),
      slider('filterEnv.sustain'),
      slider('filterEnv.release'),
    ),

    panel(
      'LFO',
      '2',
      knob('lfo.rate'),
      knob('lfo.depth'),
      select('lfo.shape', [
        ['Sine', '0'],
        ['Triangle', '1'],
        ['Square', '2'],
        ['Ramp', '3'],
        ['S&H', '4'],
      ]),
      select('lfo.sync', [
        ['Free', '0'],
        ['Sync', '1'],
      ]),
      select('lfo.dest', [
        ['— None —', NO_DEST],
        ...modulatableParamIds().map((id) => [prettyDest(id), id] as [string, string]),
      ]),
    ),

    panel(
      'Voicing',
      '3',
      select('voicing.mode', [
        ['Poly', '0'],
        ['Mono', '1'],
      ]),
      knob('voicing.glide'),
    ),

    panel('Master', '1', knob('master.volume'), knob('master.drive')),

    buildSequencerPanel(),

    buildKeyboardPanel(),
  );
}

function buildSequencerPanel(): HTMLElement {
  const transport = document.createElement('div');
  transport.className = 'transport';
  transport.innerHTML = `
    <button class="btn" data-seq="play">Play</button>
    <button class="btn" data-seq="stop">Stop</button>
    <button class="btn" data-seq="tap">Tap</button>`;

  const grid = document.createElement('div');
  grid.className = 'seq-row';
  grid.dataset.seqGrid = '1';

  return panel(
    'Sequencer',
    '2',
    transport,
    knob('seq.bpm'),
    knob('seq.swing'),
    knob('seq.steps'),
    grid,
  );
}

function buildKeyboardPanel(): HTMLElement {
  const info = document.createElement('div');
  info.className = 'kbd-info';
  info.innerHTML = `
    <span data-octave-display>Octave: 4</span>
    <span>Keys: A–L · Z/X octave · or mouse/MIDI</span>
    <span data-midi-status>MIDI: —</span>`;

  const kbd = document.createElement('div');
  kbd.className = 'keyboard';
  kbd.dataset.keyboard = '1';

  const p = panel('Keyboard', '2', info, kbd);
  p.id = 'keyboard-panel';
  return p;
}
